import { Avatar, Box, Chip, Divider, IconButton, List, ListItemButton, ListItemIcon, ListItemText, Stack, Tooltip, Typography, useTheme } from '@mui/material';
import { Logout as LogoutIcon, MoreVert as MoreVertIcon } from '@mui/icons-material';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth, type Portal } from '@/auth/AuthContext';
import { navTreeFor } from './navItems';

/**
 * AppShell — sidebar + topbar wrapper used by BOTH portals. The portal
 * is passed in as a prop rather than read from context so a Platform
 * admin who briefly inspects a tenant view (future "switch tenant"
 * feature) renders the right tree without us re-mounting auth.
 *
 * Layout: 240px fixed sidebar, no topbar height variation. Premium
 * portals tend to keep the chrome static so the eye locks onto the
 * page content; a sliding/fading topbar would cost more than it earns.
 *
 * Brand identity is conveyed by the brand mark in the sidebar header,
 * not a colored topbar — we want a quiet chrome and a loud canvas.
 */

const SIDEBAR_WIDTH = 240;

export function AppShell({ portal }: { portal: Portal }) {
  const theme = useTheme();
  const { claims, signOut } = useAuth();
  const navigate = useNavigate();
  const sections = navTreeFor(portal);

  const handleSignOut = () => {
    signOut();
    navigate('/login', { replace: true });
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* SIDEBAR */}
      <Box
        component="aside"
        sx={{
          width: SIDEBAR_WIDTH,
          flexShrink: 0,
          bgcolor: theme.palette.surface.card,
          borderRight: `1px solid ${theme.palette.border.subtle}`,
          display: 'flex',
          flexDirection: 'column',
          position: 'sticky',
          top: 0,
          height: '100vh',
        }}
      >
        {/* Brand */}
        <Box sx={{ px: 3, py: 2.5, display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: theme.radius.sm + 'px',
              bgcolor: theme.palette.brand.main,
              display: 'grid',
              placeItems: 'center',
              color: theme.palette.brand.contrastText,
              fontWeight: 700,
              fontSize: 14,
              letterSpacing: -0.4,
            }}
          >
            AS
          </Box>
          <Stack spacing={0} sx={{ minWidth: 0 }}>
            <Typography variant="body2" fontWeight={600} sx={{ lineHeight: 1.1, letterSpacing: -0.2 }}>
              AyushSetu
            </Typography>
            <Typography variant="caption" color="text.tertiary" sx={{ lineHeight: 1.1 }}>
              ABDM Connector
            </Typography>
          </Stack>
          <Chip
            label={portal === 'platform' ? 'Platform' : 'Tenant'}
            size="small"
            sx={{
              ml: 'auto',
              bgcolor: portal === 'platform' ? theme.palette.brand.lighter : theme.palette.accent.light,
              color: portal === 'platform' ? theme.palette.brand.dark : theme.palette.accent.dark,
              fontSize: 10,
              height: 20,
            }}
          />
        </Box>
        <Divider sx={{ borderColor: theme.palette.border.subtle }} />

        {/* Nav */}
        <Box sx={{ flex: 1, overflowY: 'auto', px: 1.5, py: 2 }}>
          {sections.map((section) => (
            <Box key={section.label} sx={{ mb: 2 }}>
              <Typography
                variant="overline"
                sx={{ px: 1.5, color: 'text.tertiary', display: 'block', mb: 0.5 }}
              >
                {section.label}
              </Typography>
              <List dense disablePadding>
                {section.items.map((item) => (
                  <ListItemButton
                    key={item.id}
                    component={NavLink}
                    to={item.to}
                    end={item.to === '/platform' || item.to === '/tenant'}
                    sx={{
                      borderRadius: theme.radius.sm + 'px',
                      mb: 0.25,
                      px: 1.5,
                      py: 0.875,
                      color: 'text.secondary',
                      transition: `background-color ${theme.motion.durations.fast}ms ${theme.motion.easings.standard}, color ${theme.motion.durations.fast}ms ${theme.motion.easings.standard}`,
                      '&:hover': { bgcolor: theme.palette.surface.sunken, color: 'text.primary' },
                      '&.active': {
                        bgcolor: theme.palette.brand.lighter,
                        color: theme.palette.brand.dark,
                        '& .MuiListItemIcon-root': { color: theme.palette.brand.main },
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 30, color: 'inherit' }}>
                      <item.icon sx={{ fontSize: 18 }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{ fontSize: 13, fontWeight: 500 }}
                    />
                  </ListItemButton>
                ))}
              </List>
            </Box>
          ))}
        </Box>

        {/* User chip */}
        <Divider sx={{ borderColor: theme.palette.border.subtle }} />
        <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <Avatar
            sx={{
              width: 30,
              height: 30,
              bgcolor: theme.palette.surface.sunken,
              color: 'text.primary',
              fontSize: 12,
              fontWeight: 600,
            }}
          >
            {((claims?.name ?? claims?.username ?? claims?.sub ?? '?') as string).slice(0, 2).toUpperCase()}
          </Avatar>
          <Stack spacing={0} sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="body2" noWrap sx={{ fontSize: 12, fontWeight: 500, lineHeight: 1.1 }}>
              {claims?.name ?? claims?.username ?? 'unknown'}
            </Typography>
            <Typography variant="caption" color="text.tertiary" noWrap sx={{ lineHeight: 1.1 }}>
              {claims?.organizationName ?? claims?.role ?? 'no role'}
            </Typography>
          </Stack>
          <Tooltip title="Sign out" arrow>
            <IconButton size="small" onClick={handleSignOut} aria-label="sign out">
              <LogoutIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* CONTENT — Outlet renders the route's PageShell */}
      <Box component="main" sx={{ flex: 1, minWidth: 0 }}>
        <Outlet />
      </Box>
    </Box>
  );
}

// Make MoreVertIcon import survive tree-shaking — it's exported for nav-item action menus in PR-B.
export { MoreVertIcon as _NavMoreIcon };
